import React, { useEffect, useRef, useState } from 'react'
import { Button, FormHelperText, Grid, TextField, Typography } from '@mui/material'
import { useDispatch } from 'react-redux'
import { useSnackbar } from 'notistack'
import { fetchUser } from 'modules/User/actions'
import api from 'utils/api'

const profileFields = ['firstName', 'lastName', 'phone']
const draftFrom = contact => Object.fromEntries(profileFields.map(field => [field, contact?.[field] || '']))

export default function ContactProfileEditor({ contact, editable, disabled = false, requireComplete = false, onSaved }) {
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(() => draftFrom(contact))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const pending = useRef(false)
  const mounted = useRef(true)
  useEffect(() => { mounted.current = true; return () => { mounted.current = false } }, [])
  const complete = profileFields.every(field => contact?.[field]?.trim())
  const showEditor = editable && (editing || (requireComplete && !complete))
  const locked = disabled || saving || !editable
  useEffect(() => {
    if (!editing) setDraft(draftFrom(contact))
  }, [editing, contact])

  const save = async () => {
    if (locked || pending.current) return
    const profile = Object.fromEntries(profileFields.map(field => [field, draft[field].trim()]))
    if (!profileFields.every(field => profile[field])) {
      setError('Enter your first name, last name, and phone.')
      return
    }
    pending.current = true
    setSaving(true)
    setError('')
    try {
      await api.post('/user/profile', profile)
      dispatch(fetchUser())
      if (!mounted.current) return
      onSaved(profile)
      setEditing(false)
      enqueueSnackbar('Your profile was saved.', { variant: 'success' })
    } catch (failure) {
      if (mounted.current) setError(failure.message || 'Your profile could not be saved.')
    } finally {
      pending.current = false
      if (mounted.current) setSaving(false)
    }
  }

  return <>
    <Typography fontWeight={600} sx={{ mb: 1 }}>{editable ? 'Your profile' : "Account owner's profile"}</Typography>
    {showEditor ? <>
      <Grid container spacing={1.5}>
        {profileFields.map((field, index) => <Grid item xs={12} sm={6} key={field}>
          <TextField fullWidth required size="small" disabled={locked} label={['First name', 'Last name', 'Phone'][index]}
            type={field === 'phone' ? 'tel' : 'text'} value={draft[field]}
            onChange={event => { setEditing(true); setDraft(current => ({ ...current, [field]: event.target.value })) }} />
        </Grid>)}
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="Email" value={contact?.email || ''} disabled />
        </Grid>
      </Grid>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
        Saving your name and phone here updates your user profile wherever it appears.
      </Typography>
      {error && <FormHelperText error>{error}</FormHelperText>}
      <Button type="button" variant="outlined" disabled={locked} onClick={save} sx={{ mt: 1.5 }}>
        {saving ? 'Saving profile…' : 'Save my profile'}
      </Button>
      {complete && <Button type="button" disabled={saving} onClick={() => { setEditing(false); setError('') }} sx={{ mt: 1.5 }}>Cancel</Button>}
    </> : <>
      <Typography variant="body2">Name: {[contact?.firstName, contact?.lastName].filter(Boolean).join(' ') || 'Not provided'}</Typography>
      <Typography variant="body2">Email: {contact?.email || 'Not provided'}</Typography>
      <Typography variant="body2">Phone: {contact?.phone || 'Not provided'}</Typography>
      {editable && <Button type="button" disabled={locked} onClick={() => setEditing(true)} sx={{ mt: 1 }}>Edit my profile</Button>}
    </>}
    {requireComplete && !complete && <FormHelperText error>Complete and save your name and phone here before creating your first business.</FormHelperText>}
  </>
}
